import React, { useEffect, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { FormControl, InputLabel, MenuItem, Select, Box, Typography } from '@mui/material';
import http from 'Utils/http';

const TOTAL_EXECUTION_KEY = 'Total flow execution time';

const FlowPerformanceGraph = ({item}) => {
  const [selectedPeriod, setSelectedPeriod] = useState('365');
  const [dateRange, setDateRange] = useState('');
  const [currentDateRange, setCurrentDateRange] = useState('');
  const [avgTime, setAvgTime] = useState(0);
  const [totalExecutions, setTotalExecutions] = useState(0);
  const [selectedData, setSelectedData] = useState([]);

  const handleChange = (event) => {
    setSelectedPeriod(event.target.value);
  };

  useEffect(() => {
    if (item && dateRange !== currentDateRange) {
      setAvgTime(0);
      setTotalExecutions(0);
      setCurrentDateRange(dateRange);

      http.get(`/statistics/flow/${item.id}/performance/${dateRange}`).then((res) => {
        const statistics = res.data.statistics || [];

        // Calculate average time and total executions
        let totalTime = 0;
        let executionsCount = 0;

        statistics.forEach(stat => {
          totalTime += parseFloat(stat.total_time) || 0;
          executionsCount += parseInt(stat.total_executions) || 0;
        });

        setAvgTime(executionsCount > 0 ? (totalTime / executionsCount).toFixed(3) : 0);
        setTotalExecutions(executionsCount);

        // Group by date and aggregate handler data
        const groupedData = statistics.reduce((acc, stat) => {
          const date = new Date(stat.executed_at);
          const totalTimeForRun = parseFloat(stat.total_time) || 0;
          let key;

          switch(selectedPeriod) {
            case '30':
              key = date.getDate();
              break;
            case '7':
              key = date.toLocaleDateString('en-us', { weekday: 'long' });
              break;
            case '365':
              key = date.toLocaleString('en-us', { month: 'long' });
              break;
            default:
              key = 'Today';
          }

          const existingEntry = acc.find(item => item.name === key);

          if (existingEntry) {
            existingEntry[TOTAL_EXECUTION_KEY] = (existingEntry[TOTAL_EXECUTION_KEY] || 0) + totalTimeForRun;
            // Aggregate handler stats
            if (stat.details && Array.isArray(stat.details)) {
              stat.details.forEach(detail => {
                const handlerName = detail.handler_type?.handler_name || 'Unknown';
                if (!existingEntry[handlerName]) {
                  existingEntry[handlerName] = 0;
                }
                existingEntry[handlerName] += parseFloat(detail.total_time) || 0;
              });
            }
          } else {
            const newEntry = { name: key, [TOTAL_EXECUTION_KEY]: totalTimeForRun };

            if (stat.details && Array.isArray(stat.details)) {
              stat.details.forEach(detail => {
                const handlerName = detail.handler_type?.handler_name || 'Unknown';
                newEntry[handlerName] = parseFloat(detail.total_time) || 0;
              });
            }

            acc.push(newEntry);
          }

          return acc;
        }, []);

        // Generate default array based on period
        const defaultArray = (() => {
          const currentDate = new Date();
          const currentDayIndex = currentDate.getDay();
          const currentMonthIndex = currentDate.getMonth();

          const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
          const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

          switch (selectedPeriod) {
            case '30':
              return [...Array(30)].map((_, i) => {
                const date = new Date(currentDate);
                date.setDate(currentDate.getDate() - i);
                return { name: date.getDate() };
              }).reverse();
            case '7':
              return [...daysOfWeek.slice(currentDayIndex + 1), ...daysOfWeek.slice(0, currentDayIndex + 1)].map(name => ({ name }));
            case '365':
              return [...months.slice(currentMonthIndex + 1), ...months.slice(0, currentMonthIndex + 1)].map(name => ({ name }));
            default:
              return [];
          }
        })();

        // Merge grouped data with default array
        const mergedData = defaultArray.map(defaultItem => {
          const found = groupedData.find(item => item.name === defaultItem.name);
          return found || defaultItem;
        });

        setSelectedData(mergedData);
      }).catch((err) => {
        console.error(err);
      });
    }
  }, [item, selectedPeriod, dateRange, currentDateRange]);

  const formatDate = (date) => {
    const year = date.getFullYear();
    let month = date.getMonth() + 1;
    let day = date.getDate();

    if (month < 10) {
      month = `0${month}`;
    }
    if (day < 10) {
      day = `0${day}`;
    }

    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    const getDateRange = (period) => {
      const today = new Date();
      let startDate, endDate;

      switch (period) {
        case '1':
          return { date: formatDate(today) };
        case '7':
          startDate = new Date(today);
          startDate.setDate(today.getDate() - 7);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(today);
          endDate.setHours(23, 59, 59, 999);
          break;
        case '30':
          startDate = new Date(today);
          startDate.setDate(today.getDate() - 30);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(today);
          endDate.setHours(23, 59, 59, 999);
          break;
        case '365':
          startDate = new Date(today);
          startDate.setDate(today.getDate() - 365);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(today);
          endDate.setHours(23, 59, 59, 999);
          break;
        default:
          return { date: formatDate(today) };
      }

      return {
        date: `${formatDate(startDate)}...${formatDate(endDate)}`,
      }
    };
    const range = getDateRange(selectedPeriod);
    setDateRange(range.date);
  }, [selectedPeriod]);

  // Get all unique handler names from data
  const handlerKeys = React.useMemo(() => {
    const keys = new Set();
    selectedData.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key !== 'name') {
          keys.add(key);
        }
      });
    });
    return Array.from(keys);
  }, [selectedData]);

  // Color palette for handlers
  const colors = [
    '#F4B156', // Total flow execution time
    '#E07B6A', // Request processing
    '#c191ff', // Loading data from editor
    '#2FDE8A', // Data Transformation
    '#FF5733', // Decoding flow data
    '#1fa3d2', // Response processing
    '#00CED1', // Result processing
  ];

  const getColor = (index) => {
    return colors[index % colors.length];
  };

  const colorMap = React.useMemo(() => {
    return handlerKeys.reduce((acc, key, index) => {
      acc[key] = getColor(index);
      return acc;
    }, {});
  }, [handlerKeys]);

  const handlerTotals = React.useMemo(() => {
    return handlerKeys.reduce((acc, key) => {
      const total = selectedData.reduce((sum, item) => {
        const value = parseFloat(item[key]) || 0;
        return sum + value;
      }, 0);
      acc[key] = total;
      return acc;
    }, {});
  }, [handlerKeys, selectedData]);

  const stackedHandlerKeys = React.useMemo(() => {
    return handlerKeys
      .filter((key) => key !== TOTAL_EXECUTION_KEY)
      .sort((a, b) => (handlerTotals[b] || 0) - (handlerTotals[a] || 0));
  }, [handlerKeys, handlerTotals]);

  return (
    <Box
      sx={{
        padding: '20px',
      }}
    >
      <Box sx={{display: 'flex', alignItems: 'center', marginBottom: '24px'}}>
        <FormControl>
          <InputLabel id="performance-select-label">Period</InputLabel>
          <Select
              labelId="performance-select-label"
              id="performance-select"
              name='period'
              value={selectedPeriod}
              label="Period"
              onChange={handleChange}
          >
            <MenuItem value='1'>Today</MenuItem>
            <MenuItem value='7'>Last 7 days</MenuItem>
            <MenuItem value='30'>Last 30 days</MenuItem>
            <MenuItem value='365'>Last 12 months</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="h6"
            sx={{
              color: '#3B465C',
              marginLeft: '20px',
              flexShrink: 0,
            }}
        >
          Executions: {totalExecutions} | Avg Time: {avgTime}s
        </Typography>
      </Box>
      <Box
        sx={{
          marginLeft: '-34px',
          '& .recharts-legend-item-text': {
            fontFamily: 'Roboto',
          },
          '& .recharts-text': {
            fontFamily: 'Roboto',
          },
          '& .recharts-tooltip-wrapper': {
            fontFamily: 'Roboto',
          },
          '& .recharts-legend-item': {
            marginRight: '40px !important',
          },
          '& .recharts-legend-wrapper': {
            left: '35px !important',
          },
        }}
      >
        <ResponsiveContainer width="100%" height={450}>
          <BarChart data={selectedData} barGap={-20} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3"/>
            <XAxis dataKey="name"/>
            <YAxis label={{ value: 'Time (seconds)', angle: -90, position: 'insideLeft' }}/>
            <Tooltip formatter={(value) => `${value}s`}/>
            <Legend
              verticalAlign="bottom"
              align="center"
              iconSize={10}
              iconType="square"
            />
            { handlerKeys.includes(TOTAL_EXECUTION_KEY) && (
              <Bar
                key={TOTAL_EXECUTION_KEY}
                dataKey={TOTAL_EXECUTION_KEY}
                fill={colorMap[TOTAL_EXECUTION_KEY]}
                stroke={colorMap[TOTAL_EXECUTION_KEY]}
                fillOpacity={0.55}
                barSize={20}
              />
            )}
            { stackedHandlerKeys.map((key) => (
              <Bar
                key={key}
                dataKey={key}
                fill={colorMap[key]}
                barSize={20}
                stackId="a"
              />
            )) }
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default FlowPerformanceGraph;
